/**
 * BookingReceipt.jsx
 *
 * An off-screen receipt element that gets captured by html2canvas and
 * converted to a PDF by jsPDF.  It is never actually visible in the DOM
 * — it's rendered into a hidden absolute div, snapshotted, then removed.
 *
 * Usage:
 *   import { downloadReceipt } from './BookingReceipt';
 *   await downloadReceipt({ bookingRef, selectedMover, serviceDetails,
 *                            bookingDetails, paymentInfo });
 */

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(n ?? 0);

const fmtDate = (d) => {
  if (!d) return 'Not specified';
  return new Date(d).toLocaleDateString('en-CA', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
};

// ─── build receipt HTML string ────────────────────────────────────────────────
function buildReceiptHTML({ bookingRef, selectedMover, serviceDetails, bookingDetails, paymentInfo }) {
  const price   = selectedMover?.price ?? 0;
  const total   = Number(paymentInfo?.total || paymentInfo?.deposit || price);
  const now     = new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });

  return `
  <div style="
    width: 680px;
    font-family: 'Segoe UI', Arial, sans-serif;
    background: #ffffff;
    color: #1a202c;
    padding: 0;
    margin: 0;
  ">

    <!-- ── Header bar ── -->
    <div style="
      background: linear-gradient(135deg, #1a6b3c 0%, #22a65a 60%, #34d070 100%);
      padding: 36px 40px 28px;
      color: white;
    ">
      <div style="display:flex; justify-content:space-between; align-items:flex-start;">
        <div>
          <div style="font-size:28px; font-weight:800; letter-spacing:-0.5px;">MOVD</div>
          <div style="font-size:13px; opacity:0.85; margin-top:2px;">Moving Made Simple</div>
        </div>
        <div style="text-align:right;">
          <div style="font-size:11px; opacity:0.8; letter-spacing:1.5px; text-transform:uppercase;">Booking Receipt</div>
          <div style="font-size:22px; font-weight:800; letter-spacing:2px; margin-top:4px;">${bookingRef}</div>
          <div style="font-size:11px; opacity:0.75; margin-top:4px;">Issued ${now}</div>
        </div>
      </div>

      <!-- confirmed badge -->
      <div style="
        margin-top:24px;
        display:inline-flex; align-items:center; gap:8px;
        background:rgba(255,255,255,0.2);
        border:1px solid rgba(255,255,255,0.4);
        border-radius:99px; padding:6px 16px;
        font-size:13px; font-weight:700;
      ">
        ✅ &nbsp;Booking Confirmed
      </div>
    </div>

    <!-- ── Body ── -->
    <div style="padding:32px 40px;">

      <!-- Move Details + Mover side by side -->
      <div style="display:flex; gap:24px; margin-bottom:28px;">

        <!-- Move details -->
        <div style="flex:1; background:#f8fafc; border-radius:12px; padding:20px; border:1px solid #e2e8f0;">
          <div style="font-size:11px; font-weight:700; color:#64748b; letter-spacing:1px; text-transform:uppercase; margin-bottom:14px;">Move Details</div>

          <div style="margin-bottom:12px;">
            <div style="font-size:11px; color:#94a3b8; font-weight:600; margin-bottom:2px;">DATE</div>
            <div style="font-size:14px; font-weight:700;">${fmtDate(serviceDetails?.moveDate)}</div>
          </div>

          <div style="margin-bottom:12px;">
            <div style="font-size:11px; color:#94a3b8; font-weight:600; margin-bottom:2px;">ARRIVAL WINDOW</div>
            <div style="font-size:14px; font-weight:700;">${bookingDetails?.preferredTime || 'To be confirmed'}</div>
          </div>

          <div style="margin-bottom:12px;">
            <div style="font-size:11px; color:#94a3b8; font-weight:600; margin-bottom:2px;">PICKUP</div>
            <div style="font-size:13px; font-weight:600;">${serviceDetails?.origin || '—'}</div>
          </div>

          <div>
            <div style="font-size:11px; color:#94a3b8; font-weight:600; margin-bottom:2px;">DROP-OFF</div>
            <div style="font-size:13px; font-weight:600;">${serviceDetails?.destination || '—'}</div>
          </div>
        </div>

        <!-- Mover info -->
        <div style="flex:1; background:#f8fafc; border-radius:12px; padding:20px; border:1px solid #e2e8f0;">
          <div style="font-size:11px; font-weight:700; color:#64748b; letter-spacing:1px; text-transform:uppercase; margin-bottom:14px;">Your Mover</div>

          <div style="display:flex; align-items:center; gap:12px; margin-bottom:14px;">
            <div style="
              width:44px; height:44px; border-radius:50%;
              background:#1a6b3c; color:white;
              display:flex; align-items:center; justify-content:center;
              font-size:20px; font-weight:800;
            ">${selectedMover?.company?.charAt(0) ?? 'M'}</div>
            <div>
              <div style="font-size:15px; font-weight:700;">${selectedMover?.company ?? '—'}</div>
              <div style="font-size:12px; color:#64748b;">⭐ ${selectedMover?.rating ?? '—'} &nbsp;·&nbsp; ${selectedMover?.reviews ?? 0} reviews</div>
            </div>
          </div>

          ${(selectedMover?.services ?? []).slice(0, 4).map(s => `
            <div style="
              display:inline-block; margin:3px 3px 0 0;
              background:white; border:1px solid #cbd5e1;
              border-radius:99px; padding:3px 10px;
              font-size:11px; color:#475569; font-weight:600;
            ">${s}</div>
          `).join('')}

          <div style="margin-top:14px;">
            <div style="font-size:11px; color:#94a3b8; font-weight:600; margin-bottom:2px;">CONTACT</div>
            <div style="font-size:13px;">${bookingDetails?.contactInfo?.email || '—'}</div>
            <div style="font-size:13px;">${bookingDetails?.contactInfo?.phone || '—'}</div>
          </div>
        </div>
      </div>

      <!-- Payment summary -->
      <div style="background:#0f172a; border-radius:12px; padding:24px; color:white; margin-bottom:28px;">
        <div style="font-size:11px; opacity:0.6; letter-spacing:1.5px; text-transform:uppercase; margin-bottom:8px;">Total paid</div>
        <div style="font-size:32px; font-weight:800; color:#4ade80;">${fmt(total)}</div>
        <div style="font-size:12px; opacity:0.7; margin-top:8px;">Payment received and held securely in escrow.</div>
        <div style="font-size:12px; opacity:0.7; margin-top:4px;">${paymentInfo?.method === 'credit-card' ? 'Paid by Credit Card' : (paymentInfo?.method ?? 'Card')}</div>
        <div style="border-top:1px solid rgba(255,255,255,0.15); margin-top:18px; padding-top:14px; font-size:12px; opacity:0.7;">
          No later charge is scheduled.
        </div>
      </div>

      <!-- What's next -->
      <div style="margin-bottom:28px;">
        <div style="font-size:13px; font-weight:700; color:#1a202c; margin-bottom:14px;">What Happens Next</div>
        ${[
          ['📞', 'Mover confirms within 24 hrs', 'They will call to lock in arrival time and review any special requirements.'],
          ['📦', 'Prepare for moving day', 'Pack boxes, label rooms, and set aside items that need special handling.'],
          ['🚛', 'Moving day', 'Movers arrive during your selected window.'],
          ['💳', 'Confirm completion after the move', 'Approve completion when your move is finished so the escrow process can complete.'],
        ].map(([icon, title, desc]) => `
          <div style="display:flex; gap:12px; margin-bottom:14px; align-items:flex-start;">
            <div style="
              width:36px; height:36px; border-radius:50%; flex-shrink:0;
              background:#f1f5f9; display:flex; align-items:center; justify-content:center;
              font-size:16px;
            ">${icon}</div>
            <div style="padding-top:4px;">
              <div style="font-size:13px; font-weight:700; margin-bottom:2px;">${title}</div>
              <div style="font-size:12px; color:#64748b;">${desc}</div>
            </div>
          </div>
        `).join('')}
      </div>

      <!-- Footer -->
      <div style="
        border-top:1px solid #e2e8f0; padding-top:20px;
        display:flex; justify-content:space-between; align-items:center;
      ">
        <div>
          <div style="font-size:12px; color:#64748b;">🔒 Deposit held securely in escrow</div>
          <div style="font-size:12px; color:#64748b; margin-top:4px;">🛡 Booking protected by MOVD guarantee</div>
        </div>
        <div style="text-align:right;">
          <div style="font-size:12px; color:#64748b;">support@movd.ca</div>
          <div style="font-size:11px; color:#94a3b8; margin-top:2px;">movd.ca</div>
        </div>
      </div>

    </div>
  </div>
  `;
}

// ─── main export ──────────────────────────────────────────────────────────────
export async function downloadReceipt(data) {
  // 1. Create hidden container
  const container = document.createElement('div');
  container.style.cssText = `
    position: fixed; top: -9999px; left: -9999px;
    width: 680px; background: white; z-index: -1;
  `;
  container.innerHTML = buildReceiptHTML(data);
  document.body.appendChild(container);

  try {
    // 2. Capture
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
    });

    // 3. Build PDF (A4 portrait)
    const imgData  = canvas.toDataURL('image/png');
    const pdf      = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pdfW     = pdf.internal.pageSize.getWidth();
    const pdfH     = (canvas.height * pdfW) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 0, pdfW, pdfH);
    pdf.save(`MOVD-Receipt-${data.bookingRef}.pdf`);
  } finally {
    document.body.removeChild(container);
  }
}
